import React, { useState, useEffect } from 'react'
import { 
  Grid, 
  Typography, 
  Button, 
  useTheme, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material'
import BrandedPageHeader from 'components/Layout/BrandedPageHeader'
import AddIcon from '@mui/icons-material/Add'
import { specialtyMappings } from './mappings'

const SalaryPage = () => {
  const theme = useTheme()
  const [selectedSpecialty, setSelectedSpecialty] = useState('')

  // Get specialties from the mappings file
  const specialties = Object.keys(specialtyMappings)

  const handleSpecialtyChange = (event) => {
    setSelectedSpecialty(event.target.value)
  }

  // Load embedded script when a specialty with mapping is selected
  useEffect(() => {
    const mapping = specialtyMappings[selectedSpecialty]
    
    if (mapping && mapping.script) {
      // Force a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Remove any existing Datawrapper scripts
        const existingScripts = document.querySelectorAll('script[src*="datawrapper.dwcdn.net"]')
        existingScripts.forEach(script => script.remove())
        
        // Clear the specific container
        const container = document.getElementById(mapping.container.id)
        if (container) {
          container.innerHTML = ''
        }
        
        // Create and add the new script
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.defer = true
        script.src = mapping.script.src
        script.charset = 'utf-8'
        script.setAttribute('data-target', mapping.script.target)
        
        document.head.appendChild(script)
      }, 100)
      
      return () => {
        clearTimeout(timer)
        const scriptToRemove = document.querySelector(`script[src="${mapping.script.src}"]`)
        if (scriptToRemove) {
          scriptToRemove.remove()
        }
      }
    }
  }, [selectedSpecialty])

  const renderSpecialtyContent = () => {
    if (!selectedSpecialty) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Choose a specialty to view salary data
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Select from the dropdown above to explore salary information by geography
          </Typography>
        </Box>
      )
    }

    // Check if specialty has an embedded script mapping
    const mapping = specialtyMappings[selectedSpecialty]
    
    if (mapping) {
      return (
        <Box sx={{ py: 2 }} key={`${selectedSpecialty}-${Date.now()}`}>       
          {/* Embedded script for specialty */}
          <Box sx={{ 
            minHeight: mapping.container.minHeight,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <div 
              style={{ minHeight: mapping.container.minHeight }} 
              id={mapping.container.id}
            >
              <noscript>
                <img src={mapping.fallback.src} alt={`${selectedSpecialty} salary data by geography`} />
              </noscript>
            </div>
          </Box>
        </Box>
      )
    }

    return null
  }

  return (
    <Box>
      <BrandedPageHeader>
        <Grid container spacing={0} sx={{ px: 0 }}>
          <Grid item xs={12} md={8} sx={{ pl: 0 }}>
            <Typography variant='h5' sx={{ color: 'white', fontWeight: 'bold', mb: 1, pl: 0 }}>
              Salary Transparency
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1, pl: 0 }}>
              We're gathering real-world salary data to bring transparency to the medical community. Your anonymous contributions help create a fair and informed future for healthcare professionals.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              pl: 0
            }}
          >
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              size="medium"
              sx={{
                minWidth: { xs: '100%', md: 'auto' },
                py: 1.5,
                px: 3,
                fontSize: '1rem',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              href='https://docs.google.com/forms/d/e/1FAIpQLSf48Le6uS3sucGZaAXnx-FI89rOer2lK83LpgdDjFQT-KRx5A/viewform?usp=header'
              target='_blank'
            >
              Submit Your Salary
            </Button>
          </Grid>
        </Grid>
      </BrandedPageHeader>

      <Grid
        container
        direction='column'
        sx={{
          minHeight: '100vh',
          padding: '20px 20px',
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Specialty Selection */}
        <Grid item xs={12} sx={{ mb: 4 }}>
          <Box sx={{ maxWidth: '400px', mx: 'auto' }}>
            <FormControl fullWidth>
              <InputLabel id="specialty-select-label">Select Specialty</InputLabel>
              <Select
                labelId="specialty-select-label"
                id="specialty-select"
                value={selectedSpecialty}
                label="Select Specialty"
                onChange={handleSpecialtyChange}
                sx={{ backgroundColor: 'white' }}
              >
                {specialties.map((specialty) => (
                  <MenuItem key={specialty} value={specialty}>
                    {specialty}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* Dynamic Content Based on Specialty Selection */}
        <Grid item xs={12}>
          {renderSpecialtyContent()}
        </Grid>
      </Grid>
    </Box>
  )
}

export default SalaryPage
